/**
 * LoginPage — Clean MT username/password login.
 *
 * No registration — admin creates accounts.
 * Always reachable in MT mode (no prerequisites).
 */

import { useState, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';

export function LoginPage() {
  const { login, isLoading, error, clearError, sessionInit, sessionInitError, refreshSessionInit } =
    useAuthContext();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      clearError();

      if (!username.trim() || !password) return;

      try {
        await login(username.trim(), password);
      } catch {
        // Error is set by useAuth
      }
    },
    [username, password, login, clearError]
  );

  const relayUnavailable = Boolean(sessionInit?.multiTenant && !sessionInit.mtReady);
  const relayMessage = sessionInit?.degradedReason || sessionInitError;
  const keyPoolMessage =
    sessionInit?.mtReady && !sessionInit.kalshiConfigured
      ? sessionInit.keyUnavailableReason ||
        'Trading keys are not configured yet. Admins can still sign in and upload keys.'
      : null;

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-foreground text-2xl font-bold">G1XL Trading</h1>
          <p className="text-muted-foreground mt-2 text-sm">Sign in to your account</p>
        </div>

        <div className="bg-card border-border rounded-lg border p-6">
          {relayMessage && (
            <div className="bg-destructive/10 border-destructive/20 mb-4 rounded-lg border p-3">
              <p className="text-destructive text-sm">{relayMessage}</p>
              <button
                type="button"
                className="text-destructive mt-2 text-sm font-medium underline underline-offset-2"
                onClick={() => {
                  void refreshSessionInit();
                }}
              >
                Retry Relay Check
              </button>
            </div>
          )}

          {keyPoolMessage && (
            <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
              <p className="text-sm text-amber-400">{keyPoolMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="login-username">Username</label>
              <input
                type="text"
                id="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                autoFocus
                autoComplete="username"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                type="password"
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={isLoading || relayUnavailable || !username.trim() || !password}
              >
                {relayUnavailable ? 'Relay Unavailable' : isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="text-muted-foreground mt-4 text-center text-xs">
            Accounts are created by administrators.
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border-destructive/20 mt-4 rounded-lg border p-3">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
